import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API_URL } from '../../config/api';
import { useCurrency } from '../../context/CurrencyContext';
import './PaymentGatewayModal.css';

const GATEWAY_ICONS = {
  razorpay: '💳',
  stripe: '💳',
  paypal: '🅿️',
  paytm: '📱'
};

const PaymentGatewayModal = ({ isOpen, onClose, badge, onConfirm, type = 'badge', amount: propAmount, purpose, purposeId, billingPeriod }) => {
  const { selectedCurrency, convertPrice, formatPrice } = useCurrency();
  const [gateways, setGateways] = useState([]);
  const [selectedGateway, setSelectedGateway] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [loadingGateways, setLoadingGateways] = useState(true);
  const [error, setError] = useState('');

  const fetchEnabledGateways = useCallback(async () => {
    try {
      setLoadingGateways(true);
      const token = localStorage.getItem('token') || localStorage.getItem('specialist_token');
      const res = await axios.get(`${API_URL}/payment-gateway/enabled`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const list = res.data.gateways || [];
      setGateways(list);
      if (list.length > 0) setSelectedGateway(list[0].name);
    } catch (err) {
      console.error('Failed to load gateways:', err);
      setError('No payment gateways available. Please contact admin.');
    } finally {
      setLoadingGateways(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchEnabledGateways();
      setError('');
      setAgreedToTerms(false);
      setProcessing(false);
    }
  }, [isOpen, fetchEnabledGateways]);

  if (!isOpen) return null;

  const getTitle = () => {
    if (type === 'subscription') return 'Membership Plan Upgrade';
    if (type === 'badge' && badge) return badge.displayName;
    return 'Payment';
  };

  const getDescription = () => {
    if (type === 'subscription') return 'Complete payment to activate your membership plan. Your subscription will be activated immediately after successful payment.';
    if (type === 'badge' && badge) {
      if (badge.name === 'verified') return 'Once your company documents are reviewed and validated, a Verified Badge will be displayed on your profile.';
      if (badge.name === 'inspected') return 'On-site inspection with photo and video documentation. An Inspected Badge will be displayed on your profile.';
      if (badge.name === 'certified') return 'Documents + on-site visit + client review. A Certified Badge will be displayed on your profile.';
    }
    return '';
  };

  const getPrice = () => {
    if (propAmount) return propAmount;
    if (type === 'badge' && badge) return badge.price;
    return 0;
  };

  const getPurpose = () => purpose || (type === 'badge' ? 'badge' : 'subscription');
  const getPurposeId = () => purposeId || (badge ? badge.id : null);

  const handleSubmit = async () => {
    setError('');

    if (!agreedToTerms) {
      setError('Please agree to the terms of service.');
      return;
    }
    if (!selectedGateway) {
      setError('Please select a payment gateway.');
      return;
    }

    setProcessing(true);
    try {
      const token = localStorage.getItem('token') || localStorage.getItem('specialist_token');
      const headers = { Authorization: `Bearer ${token}` };
      const price = getPrice();

      // Step 1: Create payment order via backend
      const orderRes = await axios.post(`${API_URL}/payment-gateway/create-order`, {
        gateway: selectedGateway,
        amount: convertPrice(price),
        currency: selectedCurrency.code,
        purpose: getPurpose(),
        purposeId: getPurposeId(),
        billingPeriod: billingPeriod || 'monthly'
      }, { headers });

      const orderData = orderRes.data;

      // Step 2: Handle gateway-specific client-side flow
      if (selectedGateway === 'razorpay') {
        await handleRazorpay(orderData, price, headers);
      } else if (selectedGateway === 'stripe') {
        // Stripe redirects to hosted checkout
        window.location.href = orderData.sessionUrl;
      } else if (selectedGateway === 'paypal') {
        await handlePaypal(orderData, headers);
      } else if (selectedGateway === 'paytm') {
        handlePaytm(orderData);
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError(err.response?.data?.error || 'Payment failed. Please try again.');
      setProcessing(false);
    }
  };

  const handleRazorpay = (orderData, price, headers) => {
    return new Promise((resolve, reject) => {
      if (!window.Razorpay) {
        setError('Razorpay SDK not loaded. Please refresh the page.');
        setProcessing(false);
        return reject(new Error('Razorpay SDK missing'));
      }
      const options = {
        key: orderData.keyId,
        amount: orderData.amount,
        currency: orderData.currency,
        name: 'Gulf Constructor',
        description: getTitle(),
        order_id: orderData.orderId,
        handler: async (response) => {
          try {
            await axios.post(`${API_URL}/payment-gateway/verify`, {
              gateway: 'razorpay',
              paymentData: response
            }, { headers });
            setProcessing(false);
            onConfirm({ gateway: 'razorpay', verified: true });
            resolve();
          } catch (verifyErr) {
            setError('Payment verification failed.');
            setProcessing(false);
            reject(verifyErr);
          }
        },
        modal: {
          ondismiss: () => { setProcessing(false); resolve(); }
        }
      };
      const rzp = new window.Razorpay(options);
      rzp.open();
    });
  };

  const handlePaypal = async (orderData, headers) => {
    // Open PayPal approval in a popup
    const approvalUrl = `https://www.${orderData.mode === 'live' ? '' : 'sandbox.'}paypal.com/checkoutnow?token=${orderData.orderId}`;
    const popup = window.open(approvalUrl, 'paypal_payment', 'width=500,height=600');

    // Poll for popup close and verify
    const pollTimer = setInterval(async () => {
      if (!popup || popup.closed) {
        clearInterval(pollTimer);
        try {
          await axios.post(`${API_URL}/payment-gateway/verify`, {
            gateway: 'paypal',
            paymentData: { orderID: orderData.orderId }
          }, { headers });
          setProcessing(false);
          onConfirm({ gateway: 'paypal', verified: true });
        } catch (verifyErr) {
          setError('PayPal payment was not completed or verification failed.');
          setProcessing(false);
        }
      }
    }, 1000);
  };

  const handlePaytm = (orderData) => {
    // Paytm redirects server-side via callback
    const baseUrl = orderData.mode === 'live'
      ? 'https://securegw.paytm.in'
      : 'https://securegw-stage.paytm.in';
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = `${baseUrl}/theia/api/v1/showPaymentPage?mid=${orderData.mid}&orderId=${orderData.orderId}`;
    const fields = { MID: orderData.mid, ORDER_ID: orderData.orderId, TXN_TOKEN: orderData.txnToken, TXN_AMOUNT: orderData.amount };
    Object.entries(fields).forEach(([key, val]) => {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = key;
      input.value = val;
      form.appendChild(input);
    });
    document.body.appendChild(form);
    form.submit();
  };

  return (
    <div className="payment-modal-overlay" onClick={onClose}>
      <div className="payment-modal" onClick={(e) => e.stopPropagation()}>
        <button className="payment-modal-close" onClick={onClose}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M18 6L6 18M6 6L18 18" stroke="#6D6D6D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
        </button>

        <div className="payment-modal-header">
          <h2>{getTitle()}</h2>
          <p className="payment-description">{getDescription()}</p>
        </div>

        <div className="payment-gateway-section">
          <div className="payment-gateway-header">
            <h3>Payment Gateway</h3>
            <div className="payment-amount">
              <span className="amount-label">{selectedCurrency.symbol}</span>
              <span className="amount-value">{convertPrice(getPrice())}</span>
            </div>
          </div>

          <div className="payment-details-section">
            <h4>Select Payment Method</h4>

            {loadingGateways ? (
              <div style={{ textAlign: 'center', padding: '20px', color: '#6D6D6D' }}>Loading payment options...</div>
            ) : gateways.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '24px 16px' }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>⚙️</div>
                <div style={{ color: '#e74c3c', fontWeight: 600, fontSize: 15, marginBottom: 8 }}>Online payment is not available right now.</div>
                <div style={{ color: '#6D6D6D', fontSize: 14, lineHeight: 1.6 }}>
                  Please contact the administrator to enable payment processing.<br/>
                  You can reach us at <strong>support@gulfconstructor.com</strong>
                </div>
              </div>
            ) : (
              <div className="payment-form">
                <div className="gateway-options">
                  {gateways.map((gw) => (
                    <label key={gw.name} className={`gateway-option ${selectedGateway === gw.name ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="gateway"
                        value={gw.name}
                        checked={selectedGateway === gw.name}
                        onChange={() => setSelectedGateway(gw.name)}
                      />
                      <span className="gateway-icon">{GATEWAY_ICONS[gw.name] || '💳'}</span>
                      <span className="gateway-name">{gw.displayName}</span>
                    </label>
                  ))}
                </div>

                <div className="terms-checkbox">
                  <label className="checkbox-container">
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                    />
                    <span className="checkmark"></span>
                    <span className="terms-text">
                      By clicking "Confirm Payment" I agree to the terms of service
                    </span>
                  </label>
                </div>

                {error && (
                  <div style={{ color: '#e74c3c', fontSize: '13px', textAlign: 'center', marginTop: '8px' }}>{error}</div>
                )}
              </div>
            )}
          </div>
        </div>

        <button
          className="confirm-payment-btn"
          onClick={handleSubmit}
          disabled={processing || loadingGateways || gateways.length === 0}
        >
          {processing ? 'Processing...' : 'Confirm Payment'}
        </button>
      </div>
    </div>
  );
};

export default PaymentGatewayModal;
